import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Header, Spinner } from '@rbilabs/universal-components';
import LottieView from 'lottie-react-native';
import { useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CardModal } from 'components/card-modal';
import { StaticImages, useStaticLottieUri } from 'components/picture/static-image';
import { primitive } from 'styles/constants/primitives';
import { logger } from 'utils/logger';
import { TraceName } from 'utils/performance';
import { startTrace } from 'utils/performance/trace-lite';

// simple way to display only once per app start or web reload per design ask
let didDisplayAnimationAlready = false;

const CartLoading = ({
  setLoadingAnimationComplete,
  dataRelatedIsLoading,
}: {
  setLoadingAnimationComplete: React.Dispatch<React.SetStateAction<boolean>>;
  dataRelatedIsLoading: boolean;
}) => {
  const { bottom } = useSafeAreaInsets();
  const { formatMessage } = useIntl();
  const animationRef = useRef<LottieView>(null);
  const [loop, setLoop] = useState(false);
  const uri = useStaticLottieUri(StaticImages.FullScreenCartLoader);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [internalLoadingAnimationComplete, setInternalLoadingAnimationComplete] = useState(false);

  const setCanHide = useCallback(() => {
    didDisplayAnimationAlready = true;
    // this funky pattern is because setLoadingAnimationComplete causes animation hiding jank due to heavy parent component.
    // this sneaks in right before that to eliminate jank.
    setInternalLoadingAnimationComplete(true);

    setTimeout(() => {
      clearTimeout(timeoutRef.current);
      setLoop(true);
      setLoadingAnimationComplete(true);
      animationRef.current?.play(); // will continue to play/loop as other things outside of this file get ready if necessary
    }, 0);
  }, [setLoadingAnimationComplete, setLoop]);

  // animation should go for a minimum of 1 round but max of 10s (maybe something is wrong in that case?)
  useEffect(() => {
    if (!uri || didDisplayAnimationAlready) {
      // no animation to show. shouldn't happen.
      return setCanHide();
    }

    const trace = startTrace(TraceName.CART_LOADER_VISIBLE);
    timeoutRef.current = setTimeout(() => {
      trace?.stop();
      logger.warn(
        'Cart loading animation took longer than 4s to be hideable. Setting hidable now as something might be wrong.'
      );
      setCanHide();
    }, 4000);

    return () => {
      trace?.stop();
      clearTimeout(timeoutRef.current);
    };
  }, []);

  if (internalLoadingAnimationComplete && !dataRelatedIsLoading) {
    // this funky pattern is because setLoadingAnimationComplete causes animation hiding jank due to heavy parent component.
    // this sneaks in right before that to eliminate jank.
    return (
      <CardModal
        key="cart"
        dismissType="back"
        invertHeaderColor
        headerText={formatMessage({ id: 'yourOrder' })}
        children={<Spinner marginTop={'50%'} />}
        webAnimate={false}
        isFullScreen
      />
    );
  }

  // we can easily enable this in prod by removing the lottie from sanity and this will display
  const showSpinner = !uri || didDisplayAnimationAlready;

  return (
    <CardModal
      key="cart"
      dismissType="back"
      invertHeaderColor
      headerText={showSpinner ? formatMessage({ id: 'yourOrder' }) : undefined}
      webAnimate={false}
      isFullScreen={showSpinner}
    >
      <View
        style={{ flex: 1 }}
        testID="cart-loading"
        accessibilityLabel={formatMessage({ id: 'checkForPricing' })}
      >
        {showSpinner ? (
          <Spinner marginTop={'50%'} />
        ) : (
          <>
            <LottieView
              ref={animationRef}
              resizeMode="cover"
              hardwareAccelerationAndroid
              autoPlay
              loop={loop}
              source={{ uri }}
              style={styles.fullScreenLottie}
              onAnimationFinish={setCanHide}
              onAnimationFailure={setCanHide}
            />
            <View style={[styles.messageBoxWrap, { bottom: bottom + 10 }]}>
              <View style={styles.messageBox}>
                <Header1 style={styles.text}>{formatMessage({ id: 'oneMoment' })}</Header1>
                <Subtext style={styles.text}>
                  {formatMessage({ id: 'checkingWithRestaurant' })}
                </Subtext>
              </View>
            </View>
          </>
        )}
      </View>
    </CardModal>
  );
};

const styles = StyleSheet.create({
  fullScreenLottie: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  messageBoxWrap: {
    position: 'absolute',
    right: 0,
    left: 0,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  messageBox: {
    borderRadius: 10,
    padding: 20,
    backgroundColor: primitive.bk.$mayoEggWhite,
  },
  text: {
    textAlign: 'center',
    marginBottom: 0, //margin bottom only works if i do it here
  },
});

const Header1 = Header.withConfig({
  fontWeight: 700, // font weight only works if i do it here
});

const Subtext = Header.withConfig({
  variant: 'headerTwo',
  fontSize: 16,
});

export default CartLoading;
